import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { usePresence } from '@repo/ui';

/**
 * Where the outgoing step was SEEN: its visible part, in its positioned ancestor's coordinates, and
 * how far down its own content that part starts.
 */
interface Pin {
  top: number;
  left: number;
  width: number;
  height: number;
  /** How much of the step was scrolled out of view above the body's top edge. */
  shift: number;
}

interface StepFadeProps {
  /** The step's own id, on `data-step-fade`, for the real-app rig. No style keys off it. */
  step: string;
  /** The step is current. */
  shown: boolean;
  /** This join fades (`stepJoinFades`); false cuts. */
  animate: boolean;
  /** A count of step changes: a second change ends this step's fade at once. */
  exitKey: number;
  /** A full-bleed step whose root fills the body (`height: 100%`) needs its wrapper to fill it too. */
  fill?: boolean;
  /** The step, told whether it is fading out (to stop writing into shared page slots, R3.3). */
  children: (exiting: boolean) => ReactNode;
}

/**
 * What the person sees of `el` now: the part of it inside its parent (the scrolling step body), in
 * the coordinates of its positioned ancestor (the content column, which does not scroll).
 */
const seenOf = (el: HTMLElement): Pin | null => {
  const anchor = el.offsetParent;
  const clip = el.parentElement;
  if (!anchor || !clip) return null;
  const box = el.getBoundingClientRect();
  const at = anchor.getBoundingClientRect();
  const view = clip.getBoundingClientRect();
  const top = Math.max(box.top, view.top);
  const bottom = Math.min(box.bottom, view.bottom);
  return {
    top: top - at.top - anchor.clientTop,
    left: box.left - at.left - anchor.clientLeft,
    width: box.width,
    height: Math.max(0, bottom - top),
    shift: top - box.top,
  };
};

/**
 * One onboarding step's mount, faded (`onboarding-persistent-scene-panel-fades`, R2.2, R3).
 *
 * Always rendered, so `usePresence` sees `shown` change; the step itself is built only while the
 * hook says it is mounted. While it fades out it is inert, hidden from assistive technology and
 * takes no pointer, and it is pinned over the incoming step where it was seen, so the incoming step
 * takes its place in the flow and neither jumps.
 *
 * Pinned to the content column, which does not scroll, rather than to the step body, which does:
 * an outgoing step pinned inside the body would keep the body's old scroll height for the whole
 * fade, and a person scrolled down a long step would see the incoming one off screen, then jump to
 * it. So it is cut to the part that was visible and carries its own scroll offset, and the body
 * clamps its scroll as a cut does.
 *
 * Where it was seen is read while RENDERING the change, when the DOM still holds the previous
 * commit: in the commit itself the incoming step is already in the flow, and where it comes first
 * in the page (a step back, Launch to anything) the outgoing step has already been pushed down.
 */
const StepFade = ({ step, shown, animate, exitKey, fill = false, children }: StepFadeProps) => {
  const { mounted, exiting, ref } = usePresence(shown, { animate, exitKey });
  const node = useRef<HTMLDivElement | null>(null);
  const [pin, setPin] = useState<Pin | null>(null);
  const [lastShown, setLastShown] = useState(shown);
  if (shown !== lastShown) {
    setLastShown(shown);
    setPin(!shown && node.current ? seenOf(node.current) : null);
  }
  const attach = useCallback(
    (el: HTMLDivElement | null) => {
      node.current = el;
      ref(el);
    },
    [ref],
  );
  const pinned = exiting ? pin : null;
  // The part above the body's top edge stays scrolled away inside the pinned box.
  useLayoutEffect(() => {
    if (pinned && node.current) node.current.scrollTop = pinned.shift;
  }, [pinned]);

  if (!mounted) return null;
  const style: CSSProperties = {
    // Its own formatting context at rest too: a pinned wrapper clips (`overflow: hidden`), which
    // stops the step's first and last margins collapsing through it, and would drop the content by
    // that margin mid-fade. Contained always, the content sits at the same place either way.
    display: 'flow-root',
    ...(fill && { height: '100%' }),
    ...(exiting && { pointerEvents: 'none' }),
    ...(pinned && {
      position: 'absolute',
      top: pinned.top,
      left: pinned.left,
      width: pinned.width,
      height: pinned.height,
      overflow: 'hidden',
    }),
  };
  return (
    <div
      ref={attach}
      data-step-fade={step}
      inert={exiting}
      aria-hidden={exiting ? 'true' : undefined}
      style={style}
    >
      {children(exiting)}
    </div>
  );
};

export default StepFade;
